import React, { useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Alert, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { AuthContext } from '../context/AuthContext';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Empty access row templates used when adding repeatable land or imagery access rows.
const EMPTY_LAND_ACCESS = { project_id: '', site_id: '', expires_at: '', sites: [], isLoadingSites: false };
const EMPTY_IMAGERY_ACCESS = { project_id: '', site_id: '', category_permission: '', expires_at: '', sites: [], isLoadingSites: false };

/**
 * Extracts a readable admin panel error from FastAPI responses.
 * FastAPI can return detail as a string or as a validation error array.
 */
const getAdminPanelErrorMessage = (error) => {
  const detail = error.response?.data?.detail;

  if (typeof detail === 'string') {
    return detail;
  }

  if (Array.isArray(detail) && detail.length > 0) {
    return detail.map((item) => item.msg).filter(Boolean).join(' ') || 'Please check the invite details and try again.';
  }

  return error.response?.data?.message || 'Unable to complete invite action. Please try again.';
};

/**
 * buildAccessPayload - Converts repeatable frontend access rows into the backend invite payload shape.
 * Empty site and expiry values become null, and rows without a project are ignored.
 */
const buildAccessPayload = (rows, includeCategory = false) => {
  return rows
    .filter((row) => row.project_id)
    .map((row) => {
      const access = {
        project_id: row.project_id,
        site_id: row.site_id || null,
        expires_at: row.expires_at || null,
      };

      if (includeCategory) {
        access.category_permission = row.category_permission;
      }

      return access;
    });
};

/**
 * formatPendingInviteLabel - Builds a readable pending invite option label.
 */
const formatPendingInviteLabel = (invite) => {
  if (!invite?.expires_at) {
    return invite.email;
  }

  const expiryDate = new Date(invite.expires_at);
  const formattedExpiry = Number.isNaN(expiryDate.getTime()) ? invite.expires_at : expiryDate.toLocaleString();

  return `${invite.email} - expires ${formattedExpiry}`;
};

/**
 * AdminPanelPage Component - Allows admins to send and resend user invites with optional land and imagery access.
 */
const AdminPanelPage = () => {
  // State hooks to manage invite form fields and submission feedback.
  const [email, setEmail] = useState(''); // Store invitee email state
  const [role, setRole] = useState(''); // Store selected user role state
  const [orgId, setOrgId] = useState(''); // Store selected organisation state
  const [landAccess, setLandAccess] = useState([{ ...EMPTY_LAND_ACCESS }]); // Store repeatable land access rows
  const [imageryAccess, setImageryAccess] = useState([{ ...EMPTY_IMAGERY_ACCESS }]); // Store repeatable imagery access rows
  const [message, setMessage] = useState(''); // Store success messages
  const [error, setError] = useState(''); // Store error messages
  const [isSubmitting, setIsSubmitting] = useState(false); // State to track invite submission

  // State hooks to manage resend invite form fields and feedback.
  const [pendingInvites, setPendingInvites] = useState([]); // Store pending invite dropdown options
  const [selectedInviteId, setSelectedInviteId] = useState(''); // Store selected pending invite id for resend
  const [resendMessage, setResendMessage] = useState(''); // Store resend success messages
  const [resendError, setResendError] = useState(''); // Store resend error messages
  const [isLoadingPendingInvites, setIsLoadingPendingInvites] = useState(true); // State to track pending invite loading
  const [isResendingInvite, setIsResendingInvite] = useState(false); // State to track resend submission

  // State hooks to manage option data loaded from admin-only endpoints.
  const [roles, setRoles] = useState([]); // Store role dropdown options
  const [organisations, setOrganisations] = useState([]); // Store organisation dropdown options
  const [projects, setProjects] = useState([]); // Store project dropdown options
  const [imageryCategories, setImageryCategories] = useState([]); // Store imagery category dropdown options
  const [isLoadingOptions, setIsLoadingOptions] = useState(true); // State to track initial option loading

  const { token } = useContext(AuthContext); // Use the current access token for admin-only API calls

  /**
   * loadPendingInvites - Loads pending invite options for the resend invite form.
   */
  const loadPendingInvites = useCallback(async () => {
    setIsLoadingPendingInvites(true);
    setResendError('');

    try {
      const response = await axios.get(`${API_BASE_URL}/api/v1/invites/pending-invites`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setPendingInvites(response.data || []);
    } catch (err) {
      setResendError(getAdminPanelErrorMessage(err));
    } finally {
      setIsLoadingPendingInvites(false);
    }
  }, [token]);

  /**
   * useEffect - Loads all top-level invite dropdown options when the admin panel opens.
   */
  useEffect(() => {
    const loadInviteOptions = async () => {
      setIsLoadingOptions(true);
      setError('');
      setResendError('');

      try {
        // Send all top-level option requests with the current access token.
        const [rolesResponse, organisationsResponse, projectsResponse, imageryCategoriesResponse] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/v1/identity/user-roles`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_BASE_URL}/api/v1/identity/organisations`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_BASE_URL}/api/v1/land-management/projects/options`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_BASE_URL}/api/v1/imagery/imagery-categories`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        // Store dropdown options returned by the FastAPI endpoints.
        setRoles(rolesResponse.data || []);
        setOrganisations(organisationsResponse.data || []);
        setProjects(projectsResponse.data || []);
        setImageryCategories(imageryCategoriesResponse.data || []);
      } catch (err) {
        setError(getAdminPanelErrorMessage(err));
      } finally {
        setIsLoadingOptions(false);
      }
    };

    if (token) {
      loadInviteOptions();
      loadPendingInvites();
    }
  }, [token, loadPendingInvites]);

  /**
   * loadSitesForProject - Loads site dropdown options for a selected project.
   */
  const loadSitesForProject = async (projectId) => {
    if (!projectId) {
      return [];
    }

    const response = await axios.get(`${API_BASE_URL}/api/v1/land-management/projects/${projectId}/sites/options`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    return response.data || [];
  };

  /**
   * updateLandAccessRow - Updates one land access row and reloads site options when its project changes.
   */
  const updateLandAccessRow = async (index, field, value) => {
    const nextRows = [...landAccess];
    nextRows[index] = { ...nextRows[index], [field]: value };

    if (field === 'project_id') {
      nextRows[index].site_id = '';
      nextRows[index].sites = [];
      nextRows[index].isLoadingSites = Boolean(value);
      setLandAccess(nextRows);

      if (value) {
        try {
          const sites = await loadSitesForProject(value);
          setLandAccess((currentRows) => currentRows.map((row, rowIndex) => (
            rowIndex === index ? { ...row, sites, isLoadingSites: false } : row
          )));
        } catch (err) {
          setError(getAdminPanelErrorMessage(err));
          setLandAccess((currentRows) => currentRows.map((row, rowIndex) => (
            rowIndex === index ? { ...row, isLoadingSites: false } : row
          )));
        }
      }
      return;
    }

    setLandAccess(nextRows);
  };

  /**
   * updateImageryAccessRow - Updates one imagery access row and reloads site options when its project changes.
   */
  const updateImageryAccessRow = async (index, field, value) => {
    const nextRows = [...imageryAccess];
    nextRows[index] = { ...nextRows[index], [field]: value };

    if (field === 'project_id') {
      nextRows[index].site_id = '';
      nextRows[index].sites = [];
      nextRows[index].isLoadingSites = Boolean(value);
      setImageryAccess(nextRows);

      if (value) {
        try {
          const sites = await loadSitesForProject(value);
          setImageryAccess((currentRows) => currentRows.map((row, rowIndex) => (
            rowIndex === index ? { ...row, sites, isLoadingSites: false } : row
          )));
        } catch (err) {
          setError(getAdminPanelErrorMessage(err));
          setImageryAccess((currentRows) => currentRows.map((row, rowIndex) => (
            rowIndex === index ? { ...row, isLoadingSites: false } : row
          )));
        }
      }
      return;
    }

    setImageryAccess(nextRows);
  };

  /**
   * handleSubmit - Sends the invite request to the FastAPI invite endpoint.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setIsSubmitting(true);

    const imageryAccessPayload = buildAccessPayload(imageryAccess, true);

    // Require imagery rows with a selected project to also have an imagery category.
    if (imageryAccessPayload.some((row) => !row.category_permission)) {
      setError('Please select an imagery category for each imagery access row.');
      setIsSubmitting(false);
      return;
    }

    try {
      // Submit the invite with optional land and imagery access rows.
      const response = await axios.post(`${API_BASE_URL}/api/v1/invites/`, {
        email,
        role,
        org_id: orgId,
        land_access: buildAccessPayload(landAccess),
        imagery_access: imageryAccessPayload,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Show the backend success message, refresh pending invites, and reset the form after invite creation.
      setMessage(response.data?.message || 'Invitation sent successfully');
      setEmail('');
      setRole('');
      setOrgId('');
      setLandAccess([{ ...EMPTY_LAND_ACCESS }]);
      setImageryAccess([{ ...EMPTY_IMAGERY_ACCESS }]);
      await loadPendingInvites();
    } catch (err) {
      setError(getAdminPanelErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * handleResendInvite - Resends the selected pending invitation.
   */
  const handleResendInvite = async (e) => {
    e.preventDefault();
    setResendError('');
    setResendMessage('');
    setIsResendingInvite(true);

    try {
      // Resend the selected pending invite through the admin-only FastAPI endpoint.
      const response = await axios.post(`${API_BASE_URL}/api/v1/invites/${selectedInviteId}/resend`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setResendMessage(response.data?.message || 'Invitation resent successfully');
      setSelectedInviteId('');
      await loadPendingInvites();
    } catch (err) {
      setResendError(getAdminPanelErrorMessage(err));
    } finally {
      setIsResendingInvite(false);
    }
  };

  return (
    // Main container for admin panel content below the fixed navbar.
    <Container fluid className="admin-panel-container">
      <Row className="justify-content-center w-100">
        <Col xl={10} lg={11}>
          {/* Page header for admin-only invite tooling. */}
          <div className="admin-panel-header">
            <h2>Admin Panel</h2>
            <p>Send platform invitations and manage pending invites.</p>
          </div>

          <Card className="shadow-sm admin-panel-card admin-panel-resend-card">
            <Card.Body>
              {/* Resend invite form heading. */}
              <div className="admin-panel-card-header">
                <h4>Resend invite</h4>
                <Button type="button" variant="outline-secondary" size="sm" onClick={loadPendingInvites} disabled={isLoadingPendingInvites}>
                  {isLoadingPendingInvites ? 'Refreshing' : 'Refresh'}
                </Button>
              </div>

              {/* Show pending invite loading feedback while resend options are loading. */}
              {isLoadingPendingInvites && (
                <Alert variant="info" className="d-flex align-items-center">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading pending invites...
                </Alert>
              )}

              {/* Display resend invite error message if any. */}
              {resendError && <Alert variant="danger">{resendError}</Alert>}

              {/* Display success message if invite resend succeeds. */}
              {resendMessage && <Alert variant="success">{resendMessage}</Alert>}

              <Form onSubmit={handleResendInvite}>
                <Row className="align-items-end">
                  <Col md={9}>
                    {/* Pending invite dropdown field. */}
                    <Form.Group controlId="pending-invite" className="mb-3">
                      <Form.Label>Pending invite</Form.Label>
                      <Form.Select value={selectedInviteId} onChange={(e) => setSelectedInviteId(e.target.value)} required disabled={isLoadingPendingInvites || pendingInvites.length === 0}>
                        <option value="">{pendingInvites.length === 0 ? 'No pending invites available' : 'Select pending invite'}</option>
                        {pendingInvites.map((invite) => (
                          <option key={invite.invite_id} value={invite.invite_id}>{formatPendingInviteLabel(invite)}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    {/* Resend invite button. */}
                    <Button className="admin-panel-submit-button" variant="primary" type="submit" disabled={isResendingInvite || isLoadingPendingInvites || !selectedInviteId}>
                      {isResendingInvite ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Resending
                        </>
                      ) : (
                        'Resend invite'
                      )}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>

          <Card className="shadow-sm admin-panel-card">
            <Card.Body>
              {/* Send invite form heading. */}
              <div className="admin-panel-card-header">
                <h4>Send invite</h4>
              </div>

              {/* Show option loading feedback while admin dropdown data is loading. */}
              {isLoadingOptions && (
                <Alert variant="info" className="d-flex align-items-center">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading invite options...
                </Alert>
              )}

              {/* Display admin panel error message if any. */}
              {error && <Alert variant="danger">{error}</Alert>}

              {/* Display success message if invite creation succeeds. */}
              {message && <Alert variant="success">{message}</Alert>}

              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={4}>
                    {/* Email input field. */}
                    <Form.Group controlId="invite-email" className="mb-3">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        autoComplete="email"
                        placeholder="user@example.com"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    {/* Role dropdown field. */}
                    <Form.Group controlId="invite-role" className="mb-3">
                      <Form.Label>Role</Form.Label>
                      <Form.Select value={role} onChange={(e) => setRole(e.target.value)} required disabled={isLoadingOptions}>
                        <option value="">Select role</option>
                        {roles.map((roleOption) => (
                          <option key={roleOption} value={roleOption}>{roleOption}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    {/* Organisation dropdown field. */}
                    <Form.Group controlId="invite-organisation" className="mb-3">
                      <Form.Label>Organisation</Form.Label>
                      <Form.Select value={orgId} onChange={(e) => setOrgId(e.target.value)} required disabled={isLoadingOptions}>
                        <option value="">Select organisation</option>
                        {organisations.map((organisation) => (
                          <option key={organisation.org_id} value={organisation.org_id}>{organisation.name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Repeatable land access section. */}
                <div className="admin-panel-section">
                  <div className="admin-panel-section-header">
                    <h5>Land access</h5>
                    <Button type="button" variant="outline-primary" size="sm" onClick={() => setLandAccess([...landAccess, { ...EMPTY_LAND_ACCESS }])}>
                      Add land access
                    </Button>
                  </div>

                  {landAccess.map((row, index) => (
                    <Row className="admin-panel-access-row" key={`land-${index}`}>
                      <Col md={4}>
                        {/* Land project dropdown field. */}
                        <Form.Group controlId={`land-project-${index}`} className="mb-3">
                          <Form.Label>Project</Form.Label>
                          <Form.Select value={row.project_id} onChange={(e) => updateLandAccessRow(index, 'project_id', e.target.value)} disabled={isLoadingOptions}>
                            <option value="">No project access</option>
                            {projects.map((project) => (
                              <option key={project.project_id} value={project.project_id}>{project.project_name}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col md={4}>
                        {/* Land site dropdown field. */}
                        <Form.Group controlId={`land-site-${index}`} className="mb-3">
                          <Form.Label>Site</Form.Label>
                          <Form.Select value={row.site_id} onChange={(e) => updateLandAccessRow(index, 'site_id', e.target.value)} disabled={!row.project_id || row.isLoadingSites}>
                            <option value="">All sites under project</option>
                            {row.sites.map((site) => (
                              <option key={site.site_id} value={site.site_id}>{site.site_name}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col md={3}>
                        {/* Land access expiry field. */}
                        <Form.Group controlId={`land-expiry-${index}`} className="mb-3">
                          <Form.Label>Expiry</Form.Label>
                          <Form.Control type="datetime-local" value={row.expires_at} onChange={(e) => updateLandAccessRow(index, 'expires_at', e.target.value)} />
                        </Form.Group>
                      </Col>

                      <Col md={1} className="admin-panel-remove-col">
                        {/* Remove land access row button. */}
                        <Button type="button" variant="outline-danger" size="sm" onClick={() => setLandAccess(landAccess.filter((_, rowIndex) => rowIndex !== index))} disabled={landAccess.length === 1}>
                          Remove
                        </Button>
                      </Col>
                    </Row>
                  ))}
                </div>

                {/* Repeatable imagery access section. */}
                <div className="admin-panel-section">
                  <div className="admin-panel-section-header">
                    <h5>Imagery access</h5>
                    <Button type="button" variant="outline-primary" size="sm" onClick={() => setImageryAccess([...imageryAccess, { ...EMPTY_IMAGERY_ACCESS }])}>
                      Add imagery access
                    </Button>
                  </div>

                  {imageryAccess.map((row, index) => (
                    <Row className="admin-panel-access-row" key={`imagery-${index}`}>
                      <Col md={3}>
                        {/* Imagery project dropdown field. */}
                        <Form.Group controlId={`imagery-project-${index}`} className="mb-3">
                          <Form.Label>Project</Form.Label>
                          <Form.Select value={row.project_id} onChange={(e) => updateImageryAccessRow(index, 'project_id', e.target.value)} disabled={isLoadingOptions}>
                            <option value="">No imagery access</option>
                            {projects.map((project) => (
                              <option key={project.project_id} value={project.project_id}>{project.project_name}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col md={3}>
                        {/* Imagery site dropdown field. */}
                        <Form.Group controlId={`imagery-site-${index}`} className="mb-3">
                          <Form.Label>Site</Form.Label>
                          <Form.Select value={row.site_id} onChange={(e) => updateImageryAccessRow(index, 'site_id', e.target.value)} disabled={!row.project_id || row.isLoadingSites}>
                            <option value="">All sites under project</option>
                            {row.sites.map((site) => (
                              <option key={site.site_id} value={site.site_id}>{site.site_name}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col md={2}>
                        {/* Imagery category dropdown appears once a project is selected. */}
                        {row.project_id && (
                          <Form.Group controlId={`imagery-category-${index}`} className="mb-3">
                            <Form.Label>Category</Form.Label>
                            <Form.Select value={row.category_permission} onChange={(e) => updateImageryAccessRow(index, 'category_permission', e.target.value)} required>
                              <option value="">Select category</option>
                              {imageryCategories.map((category) => (
                                <option key={category} value={category}>{category}</option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        )}
                      </Col>

                      <Col md={3}>
                        {/* Imagery access expiry field. */}
                        <Form.Group controlId={`imagery-expiry-${index}`} className="mb-3">
                          <Form.Label>Expiry</Form.Label>
                          <Form.Control type="datetime-local" value={row.expires_at} onChange={(e) => updateImageryAccessRow(index, 'expires_at', e.target.value)} />
                        </Form.Group>
                      </Col>

                      <Col md={1} className="admin-panel-remove-col">
                        {/* Remove imagery access row button. */}
                        <Button type="button" variant="outline-danger" size="sm" onClick={() => setImageryAccess(imageryAccess.filter((_, rowIndex) => rowIndex !== index))} disabled={imageryAccess.length === 1}>
                          Remove
                        </Button>
                      </Col>
                    </Row>
                  ))}
                </div>

                {/* Submit invite button. */}
                <Button variant="primary" type="submit" disabled={isSubmitting || isLoadingOptions}>
                  {isSubmitting ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Sending invite
                    </>
                  ) : (
                    'Send invite'
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AdminPanelPage;
